/* ============================================================================
   VILLAINS TCG — Header interactions (cst-header.js)
   Header controller: sticky state, nav drawer, search drawer + native
   predictive search, desktop dropdowns, mobile accordions, announcement
   rotation. Defines a shared overlay manager (window.CST.overlay) reused by
   the cart drawer. Progressive enhancement over server-rendered markup.

   Load-order contract: cst-head.liquid loads cst-utils.js + cst-base-element.js
   (non-async modules) in <head> BEFORE the section scripts in <body>, so
   window.CST.{Element,utils,defineElement} exist here. Keep all tags non-async.
   ========================================================================== */

const Base = (window.CST && window.CST.Element) || HTMLElement;
const U = (window.CST && window.CST.utils) || {};
if (!(window.CST && window.CST.Element)) {
  console.warn('[cst] foundation not ready before cst-header.js — check script order in cst-head.liquid');
}

/** Escape text for safe innerHTML interpolation. */
function esc(str) {
  return String(str == null ? '' : str)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/* --------------------------------------------------------- background inert */
function inertBackground(panel, on) {
  const targets = [
    document.getElementById('MainContent'),
    document.querySelector('.page-wrapper > footer, footer'),
    document.querySelector('.cst-header__bar'),
  ].filter((el) => el && el !== panel && !panel.contains(el) && !el.contains(panel));
  targets.forEach((el) => { el.inert = on; });
  return targets;
}

/* ------------------------------------------------------ shared overlay mgr */
const overlay = {
  active: null,
  trigger: null,
  _release: null,
  _inerted: null,
  scrim() { return document.querySelector('[data-cst-scrim]'); },

  _cancelPending(panel) {
    if (panel._cstCloseTimer) { clearTimeout(panel._cstCloseTimer); panel._cstCloseTimer = null; }
    if (panel._cstOnEnd) { panel.removeEventListener('transitionend', panel._cstOnEnd); panel._cstOnEnd = null; }
  },

  open(panel, trigger, { backdrop = true, focus } = {}) {
    if (this.active && this.active !== panel) this.close();
    this._cancelPending(panel); // in case it was mid-close
    this.active = panel;
    this.trigger = trigger || (document.activeElement !== document.body ? document.activeElement : null);

    panel.hidden = false;
    if (backdrop) {
      const s = this.scrim();
      if (s) { this._cancelPending(s); s.hidden = false; requestAnimationFrame(() => s.classList.add('is-open')); }
    }
    document.documentElement.style.setProperty('scrollbar-gutter', 'stable');
    document.documentElement.style.overflow = 'hidden';

    if (this.trigger) this.trigger.setAttribute('aria-expanded', 'true');
    this._inerted = inertBackground(panel, true);
    if (typeof U.trapFocus === 'function') this._release = U.trapFocus(panel);

    requestAnimationFrame(() => {
      panel.classList.add('is-open');
      const target = (focus && panel.querySelector(focus))
        || panel.querySelector('[data-cst-initial-focus]')
        || panel.querySelector('input:not([type="hidden"]), button, [href], [tabindex]:not([tabindex="-1"])')
        || panel;
      target.focus?.({ preventScroll: true });
    });

    document.dispatchEvent(new CustomEvent('cst:overlay:open', { detail: { panel } }));
  },

  close() {
    const panel = this.active;
    if (!panel) return;
    this.active = null;

    const reduced = U.prefersReducedMotion && U.prefersReducedMotion();
    const hide = () => { if (overlay.active !== panel) { panel.hidden = true; } overlay._cancelPending(panel); };
    panel.classList.remove('is-open');
    if (reduced) { hide(); }
    else {
      panel._cstOnEnd = (e) => { if (e.target === panel) hide(); };
      panel.addEventListener('transitionend', panel._cstOnEnd);
      panel._cstCloseTimer = setTimeout(hide, 400);
    }

    const s = this.scrim();
    if (s) {
      s.classList.remove('is-open');
      const sHide = () => { s.hidden = true; if (s._cstOnEnd) s.removeEventListener('transitionend', s._cstOnEnd); if (s._cstCloseTimer) clearTimeout(s._cstCloseTimer); };
      if (reduced) sHide();
      else { s._cstOnEnd = (e) => { if (e.target === s) sHide(); }; s.addEventListener('transitionend', s._cstOnEnd); s._cstCloseTimer = setTimeout(sHide, 400); }
    }

    document.documentElement.style.overflow = '';
    document.documentElement.style.removeProperty('scrollbar-gutter');
    if (this._inerted) { this._inerted.forEach((el) => { el.inert = false; }); this._inerted = null; }
    if (this._release) { this._release(); this._release = null; }
    if (this.trigger) {
      this.trigger.setAttribute('aria-expanded', 'false');
      if (this.trigger.offsetParent !== null) this.trigger.focus?.({ preventScroll: true });
      this.trigger = null;
    }
  },
};

document.addEventListener('keydown', (e) => { if (e.key === 'Escape') overlay.close(); });
document.addEventListener('click', (e) => {
  if (e.target.closest('[data-cst-close]')) { e.preventDefault(); overlay.close(); }
  if (e.target.closest('[data-cst-scrim]')) overlay.close();
});

window.CST = Object.assign(window.CST || {}, { overlay });

/* ------------------------------------------------------------- <cst-header> */
class CstHeader extends Base {
  mount() {
    this.navDrawer = this.querySelector('#cst-nav-drawer');
    this.search = this.querySelector('#cst-search');
    this.searchInput = this.search && this.search.querySelector('[data-cst-search-input]');

    this.bindSticky();
    this.bindTriggers();
    this.bindDropdowns();
    this.bindMobileAccordions();
    this.bindSearch();
    this.rotateAnnouncement();
    this.publishHeaderHeight();
  }

  /* Expose header-group height as --cst-header-h (used by e.g. full-screen hero). */
  publishHeaderHeight() {
    const group = document.getElementById('header-group');
    if (!group) return;
    const set = () => {
      document.documentElement.style.setProperty('--cst-header-h', `${Math.round(group.getBoundingClientRect().height)}px`);
    };
    set();
    this.listen(window, 'resize', (U.debounce || ((f) => f))(set, 150));
  }

  bindSticky() {
    if (!this.hasAttribute('data-sticky')) return;
    const onScroll = (U.rafThrottle || ((f) => f))(() => {
      this.classList.toggle('is-scrolled', window.scrollY > 8);
    });
    this.listen(window, 'scroll', onScroll, { passive: true });
    onScroll();
  }

  bindTriggers() {
    const burger = this.querySelector('[data-cst-open-nav]');
    if (burger && this.navDrawer) this.listen(burger, 'click', () => overlay.open(this.navDrawer, burger));

    this.querySelectorAll('[data-cst-open-search]').forEach((btn) => {
      this.listen(btn, 'click', (e) => {
        e.preventDefault();
        overlay.open(this.search, btn, { backdrop: false, focus: '[data-cst-search-input]' });
      });
    });

    const cartBtn = this.querySelector('[data-cst-open-cart]');
    if (cartBtn) this.listen(cartBtn, 'click', () =>
      document.dispatchEvent(new CustomEvent('cst:cart:open', { detail: { trigger: cartBtn } }))
    );

    if (this.search) this.listen(this.search, 'click', (e) => { if (e.target === this.search) overlay.close(); });
  }

  /* desktop dropdowns — nav disclosure pattern; sync aria-expanded to visible state */
  bindDropdowns() {
    this.querySelectorAll('.cst-nav__item--has-menu').forEach((item) => {
      const link = item.querySelector('.cst-nav__link');
      const panel = item.querySelector('.cst-nav__panel');
      if (!link) return;
      const set = (open) => { link.setAttribute('aria-expanded', String(open)); if (panel) panel.classList.toggle('is-open', open); };

      // pointer + focus mirror the CSS open state onto aria
      this.listen(item, 'pointerenter', () => set(true));
      this.listen(item, 'pointerleave', () => set(false));
      this.listen(item, 'focusin', () => set(true));
      this.listen(item, 'focusout', (e) => { if (!item.contains(e.relatedTarget)) set(false); });

      // touch: first tap opens, second navigates
      this.listen(link, 'click', (e) => {
        if (link.getAttribute('aria-expanded') !== 'true') { e.preventDefault(); this.closeDropdowns(); set(true); link.focus(); }
      });
    });
    this.listen(document, 'click', (e) => { if (!e.target.closest('.cst-nav__item--has-menu')) this.closeDropdowns(); });
    this.listen(this, 'keydown', (e) => {
      if (e.key !== 'Escape') return;
      const openItem = this.querySelector('.cst-nav__link[aria-expanded="true"]');
      this.closeDropdowns();
      if (openItem && this.contains(document.activeElement)) openItem.focus();
    });
  }
  closeDropdowns() {
    this.querySelectorAll('.cst-nav__link[aria-expanded="true"]').forEach((l) => l.setAttribute('aria-expanded', 'false'));
    this.querySelectorAll('.cst-nav__panel.is-open').forEach((p) => p.classList.remove('is-open'));
  }

  bindMobileAccordions() {
    this.querySelectorAll('.cst-nav-drawer__toggle').forEach((btn) => {
      const sub = btn.parentElement.querySelector('.cst-nav-drawer__sublist');
      this.listen(btn, 'click', () => {
        const open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!open));
        if (sub) sub.hidden = open;
      });
    });
  }

  bindSearch() {
    if (!this.searchInput) return;
    const url = (window.Theme && window.Theme.routes && window.Theme.routes.predictive_search_url) || '/search/suggest';
    const run = (U.debounce || ((f) => f))(async (q) => {
      if (!q) return this.renderSearch(null, '');
      try {
        const res = await fetch(
          `${url}.json?q=${encodeURIComponent(q)}&resources[type]=product,query,collection&resources[limit]=6`,
          { headers: { Accept: 'application/json' } }
        );
        if (!res.ok) return;
        const data = await res.json();
        this.renderSearch(data.resources ? data.resources.results : null, q);
      } catch (_) { /* silent — native form still submits */ }
    }, 220);
    this.listen(this.searchInput, 'input', (e) => run(e.target.value.trim()));
  }

  renderSearch(results, q) {
    const s = this.search;
    if (!s) return;
    const hint = s.querySelector('[data-cst-search-empty]');
    const pGroup = s.querySelector('[data-cst-search-products]');
    const pList = s.querySelector('[data-cst-search-products-list]');
    const sGroup = s.querySelector('[data-cst-search-suggestions]');
    const sList = s.querySelector('[data-cst-search-suggestions-list]');
    const all = s.querySelector('[data-cst-search-all]');
    const status = s.querySelector('[data-cst-search-status]');

    const products = (results && results.products) || [];
    const queries = (results && results.queries) || [];

    if (!q) {
      if (hint) hint.hidden = false;
      if (pGroup) pGroup.hidden = true;
      if (sGroup) sGroup.hidden = true;
      if (all) all.hidden = true;
      if (status) status.textContent = '';
      return;
    }
    if (hint) hint.hidden = true;

    if (pList) pList.innerHTML = products.map((p) => {
      const img = p.image || (p.featured_image && p.featured_image.url) || '';
      const price = typeof p.price === 'string' ? p.price : '';
      return `<li><a class="cst-search__product" href="${esc(p.url)}">
        ${img ? `<img src="${esc(img)}" alt="" loading="lazy" width="44" height="44">` : ''}
        <span class="cst-search__product-title">${esc(p.title)}</span>
        ${price ? `<span class="cst-search__product-price">${esc(price)}</span>` : ''}
      </a></li>`;
    }).join('');
    if (pGroup) pGroup.hidden = products.length === 0;

    if (sList) sList.innerHTML = queries.map((qu) =>
      `<li><a class="cst-search__suggestion" href="${esc(qu.url)}">${qu.styled_text || esc(qu.text)}</a></li>`
    ).join('');
    if (sGroup) sGroup.hidden = queries.length === 0;

    if (all) {
      all.href = `${(window.Theme && window.Theme.routes && window.Theme.routes.search_url) || '/search'}?q=${encodeURIComponent(q)}`;
      all.hidden = products.length === 0 && queries.length === 0;
    }
    if (status) {
      status.textContent = (products.length + queries.length) === 0
        ? `No results for “${q}”`
        : `${products.length} products and ${queries.length} suggestions for “${q}”`;
    }
  }

  rotateAnnouncement() {
    const bar = document.querySelector('[data-cst-announcement]');
    if (!bar || this.reducedMotion) return;
    const msgs = Array.from(bar.querySelectorAll('.cst-announcement__msg'));
    if (msgs.length < 2) return;
    const seconds = parseFloat(bar.getAttribute('data-rotate')) || 5;
    let i = 0;
    let timer = null;
    const tick = () => {
      msgs[i].classList.remove('is-current');
      msgs[i].setAttribute('aria-hidden', 'true');
      i = (i + 1) % msgs.length;
      msgs[i].classList.add('is-current');
      msgs[i].removeAttribute('aria-hidden');
    };
    const start = () => { if (!timer) timer = setInterval(tick, seconds * 1000); };
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
    start();
    // Pause on hover/focus (WCAG 2.2.2 affordance)
    this.listen(bar, 'pointerenter', stop);
    this.listen(bar, 'pointerleave', start);
    this.listen(bar, 'focusin', stop);
    this.listen(bar, 'focusout', start);
    this._disposers.push(stop);
  }
}

if (window.CST && window.CST.defineElement) window.CST.defineElement('cst-header', CstHeader);
else if (!customElements.get('cst-header')) customElements.define('cst-header', CstHeader);
